/**
 * Pure logic helpers used by `rules-section.tsx`.
 *
 *   readLooseLabel   — defensively reads a label that may be plain string OR
 *                      LocalizedString (legacy: boolean labels).
 *   OPERATOR_LABELS  — Persian labels for each `ConditionOperator`.
 *   OPERATORS_BY_TYPE— allowed operators per field type.
 *   ACTION_LABELS    — Persian labels for each conditional action type.
 *   conditionSummary — render a ConditionalExpression as Persian human text.
 *   ruleSummary      — render a whole rule as Persian.
 *   detectConflicts  — find pairs of enabled rules whose actions oppose.
 *   collectFieldKeys — set of all field_keys referenced in a condition tree.
 *
 * No React, no Supabase — testable in isolation.
 */
import type {
  ConditionalRule,
  ConditionalExpression,
  ConditionOperator,
  FieldDefinition,
} from '../api/form-builder-api'

export function readLooseLabel(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') return (v as { fa?: string }).fa
  return undefined
}

export const OPERATOR_LABELS: Record<string, string> = {
  is_true: 'برابر با بله',
  is_false: 'برابر با خیر',
  equals: 'برابر با',
  not_equals: 'نابرابر با',
  greater_than: 'بزرگتر از',
  less_than: 'کوچکتر از',
  greater_than_or_equal: 'بزرگتر یا برابر با',
  less_than_or_equal: 'کوچکتر یا برابر با',
  is_empty: 'خالی است',
  is_not_empty: 'خالی نیست',
  contains: 'شامل',
  not_contains: 'شامل نمی‌شود',
  starts_with: 'شروع می‌شود با',
  ends_with: 'پایان می‌یابد با',
  matches_pattern: 'مطابق با الگو',
  contains_option: 'شامل گزینه',
  does_not_contain_option: 'شامل نمی‌شود گزینه',
  count_at_least: 'تعداد حداقل',
  count_at_most: 'تعداد حداکثر',
  count_equals: 'تعداد برابر با',
}

export const OPERATORS_BY_TYPE: Record<string, ConditionOperator[]> = {
  boolean: ['is_true', 'is_false', 'is_empty', 'is_not_empty'],
  text: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'matches_pattern', 'is_empty', 'is_not_empty'],
  textarea: ['contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty'],
  date: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty'],
  time: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty'],
  datetime: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty'],
  single_select: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
  multi_select: ['contains_option', 'does_not_contain_option', 'count_at_least', 'count_at_most', 'count_equals', 'is_empty', 'is_not_empty'],
  file: ['is_empty', 'is_not_empty'],
  image: ['is_empty', 'is_not_empty'],
  calculated: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty'],
  repeatable_array: ['count_at_least', 'count_at_most', 'count_equals', 'is_empty', 'is_not_empty'],
}

export const ACTION_LABELS: Record<string, string> = {
  show: 'نمایش',
  hide: 'پنهان‌کردن',
  require: 'اجباری‌کردن',
  unrequire: 'اختیاری‌کردن',
  enable: 'فعال‌کردن',
  disable: 'غیرفعال‌کردن',
  set_default_value: 'تنظیم مقدار پیش‌فرض',
  filter_options: 'فیلتر گزینه‌ها',
}

export function conditionSummary(
  cond: ConditionalExpression,
  fields: FieldDefinition[],
  depth = 0,
): string {
  if (!cond) return '...'

  if (cond.type === 'comparison') {
    const field = fields.find((f) => f.field_key === cond.field_key)
    const fieldLabel = field?.label.fa || cond.field_key
    const opLabel = OPERATOR_LABELS[cond.operator] || cond.operator
    const valStr = cond.compare_to_field_key
      ? `[${fields.find((f) => f.field_key === cond.compare_to_field_key)?.label.fa || cond.compare_to_field_key}]`
      : cond.value !== undefined
      ? String(cond.value)
      : ''
    return `«${fieldLabel}» ${opLabel}${valStr ? ` ${valStr}` : ''}`
  }

  if (cond.type === 'and') {
    const parts = cond.children.map((c) => conditionSummary(c, fields, depth + 1))
    const joined = parts.join(' و ')
    return depth > 0 ? `(${joined})` : joined
  }

  if (cond.type === 'or') {
    const parts = cond.children.map((c) => conditionSummary(c, fields, depth + 1))
    const joined = parts.join(' یا ')
    return depth > 0 ? `(${joined})` : joined
  }

  if (cond.type === 'not') {
    return `نه (${conditionSummary(cond.child, fields, depth + 1)})`
  }

  return '...'
}

export function ruleSummary(rule: ConditionalRule, fields: FieldDefinition[]): string {
  const condStr = conditionSummary(rule.condition, fields)
  const actionStr = rule.actions
    .map((a) => {
      const field = fields.find((f) => f.field_key === a.target_field_key)
      return `${ACTION_LABELS[a.type] || a.type} «${field?.label.fa || a.target_field_key}»`
    })
    .join('، ')
  return `اگر ${condStr}، آنگاه ${actionStr}`
}

export interface RuleConflict {
  ruleId1: string
  ruleId2: string
  target: string
  message: string
}

export function detectConflicts(
  rules: ConditionalRule[],
  fields: FieldDefinition[],
): RuleConflict[] {
  const conflicts: RuleConflict[] = []
  const enabledRules = rules.filter((r) => r.is_enabled).sort((a, b) => a.priority - b.priority)

  for (let i = 0; i < enabledRules.length; i++) {
    for (let j = i + 1; j < enabledRules.length; j++) {
      const r1 = enabledRules[i]
      const r2 = enabledRules[j]
      for (const a1 of r1.actions) {
        for (const a2 of r2.actions) {
          if (a1.target_field_key !== a2.target_field_key) continue
          const opposing = (
            (a1.type === 'show' && a2.type === 'hide') ||
            (a1.type === 'hide' && a2.type === 'show') ||
            (a1.type === 'require' && a2.type === 'unrequire') ||
            (a1.type === 'unrequire' && a2.type === 'require') ||
            (a1.type === 'enable' && a2.type === 'disable') ||
            (a1.type === 'disable' && a2.type === 'enable')
          )
          if (opposing) {
            const field = fields.find((f) => f.field_key === a1.target_field_key)
            conflicts.push({
              ruleId1: r1.rule_id,
              ruleId2: r2.rule_id,
              target: a1.target_field_key,
              message: `تعارض: قانون‌های #${r1.priority} و #${r2.priority} روی فیلد «${field?.label.fa || a1.target_field_key}» اکشن‌های متضاد دارند`,
            })
          }
        }
      }
    }
  }

  return conflicts
}

export function collectFieldKeys(cond: ConditionalExpression): Set<string> {
  const keys = new Set<string>()
  if (!cond) return keys

  if (cond.type === 'comparison') {
    keys.add(cond.field_key)
    if (cond.compare_to_field_key) keys.add(cond.compare_to_field_key)
    return keys
  }

  if (cond.type === 'and' || cond.type === 'or') {
    for (const child of cond.children) {
      for (const k of collectFieldKeys(child)) keys.add(k)
    }
    return keys
  }

  if (cond.type === 'not') {
    for (const k of collectFieldKeys(cond.child)) keys.add(k)
  }

  return keys
}
